import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../hooks/useLanguage';

interface WindowProps {
    id: string;
    title: string;
    initialState?: { x: number; y: number; width: number; height: number };
    onClose: () => void;
    onFocus: () => void;
    onMinimize?: () => void;
    onHelp?: () => void; // Nouveau : callback pour le bouton d'aide
    isActive: boolean;
    zIndex: number;
    children: React.ReactNode;
    isMinimized?: boolean;
    isAppearing?: boolean; // Nouveau : indique si la fenêtre est en cours d'apparition
    resizable?: boolean; // Nouveau : permet de désactiver le redimensionnement (défaut: true)
}

/**
 * Composant Window - Gestionnaire de fenêtres avec drag & drop, redimensionnement et persistance.
 * Simule le comportement authentique d'un gestionnaire de fenêtres des années 90.
 * 
 * @param {WindowProps} props - Propriétés du composant
 * @param {string} props.id - Identifiant unique de la fenêtre pour la persistance
 * @param {string} props.title - Titre affiché dans la barre de titre
 * @param {Object} [props.initialState] - Position et taille initiales
 * @param {Function} props.onClose - Callback appelé lors de la fermeture
 * @param {Function} props.onFocus - Callback appelé lors du focus
 * @param {Function} [props.onMinimize] - Callback appelé lors de la minimisation
 * @param {boolean} props.isActive - Indique si la fenêtre est active (au premier plan)
 * @param {number} props.zIndex - Index de profondeur pour l'empilement
 * @param {React.ReactNode} props.children - Contenu de la fenêtre
 * @param {boolean} [props.isMinimized] - Indique si la fenêtre est minimisée
 * 
 * @returns {JSX.Element|null} Composant de fenêtre ou null si minimisée
 * 
 * @example
 * <Window
 *   id="explorer"
 *   title="EXPLORATEUR_DE_FICHIERS"
 *   isActive={activeWindow === 'explorer'}
 *   zIndex={50}
 *   onClose={() => closeWindow('explorer')}
 *   onFocus={() => setActiveWindow('explorer')}
 * >
 *   <FileExplorer />
 * </Window>
 */
export const Window: React.FC<WindowProps> = ({
    id, title, initialState, onClose, onFocus, onMinimize, onHelp, isActive, zIndex, children, isMinimized, isAppearing, resizable = true
}) => {
    const { t } = useLanguage();
    // Persistent Position & Size
    const [rect, setRect] = useState(() => {
        const saved = localStorage.getItem(`window_rect_${id}`);

        if (saved) {
            // Si une position est sauvegardée, l'utiliser TOUJOURS (ignorer initialState)
            try {
                const savedRect = JSON.parse(saved);
                
                // Validation : vérifier que les dimensions sauvegardées sont raisonnables
                // Si trop petites, ignorer et utiliser les dimensions par défaut
                const minWidth = 300;
                const minHeight = 300;
                
                if (savedRect.width < minWidth || savedRect.height < minHeight) {
                    console.log(`Window ${id} - Dimensions sauvegardées trop petites (${savedRect.width}x${savedRect.height}), utilisation position par défaut`);
                    // Supprimer la sauvegarde invalide
                    localStorage.removeItem(`window_rect_${id}`);
                } else {
                    // Correction spéciale pour albion-player : forcer hauteur minimale de 650px
                    if (id === 'albion-player' && savedRect.height < 650) {
                        console.log(`Window ${id} - Hauteur sauvegardée trop petite (${savedRect.height}), mise à jour à 650px`);
                        savedRect.height = 650;
                        localStorage.setItem(`window_rect_${id}`, JSON.stringify(savedRect));
                    }
                    console.log(`Window ${id} - Position sauvegardée trouvée:`, savedRect);
                    return savedRect;
                }
            } catch (e) {
                console.error(`Erreur parsing position sauvegardée pour ${id}:`, e);
            }
        }

        // Aucune position sauvegardée - utiliser des positions par défaut simples
        // Ne plus dépendre d'initialState ou de calculs externes
        console.log(`Window ${id} - Aucune position sauvegardée, utilisation position par défaut`);

        const defaultPositions: Record<string, { x: number; y: number; width: number; height: number }> = {
            'board': { x: 136, y: 31, width: 1200, height: 800 },
            'logs': { x: 1500, y: 700, width: 400, height: 300 },
            'explorer': { x: 1349, y: 30, width: 300, height: 377 },
            'messenger': { x: 140, y: 50, width: 384, height: 566 },
            'theear': { x: 840, y: 31, width: 500, height: 374 },
            'enhancer': { x: 300, y: 200, width: 600, height: 500 },
            'wallpaper-selector': { x: 510, y: 142, width: 869, height: 697 },
            'snake': { x: 537, y: 31, width: 800, height: 820 },
            'spaceinvaders': { x: 472, y: 31, width: 860, height: 806 },
            'albion-player': { x: 639, y: 31, width: 350, height: 650 }
        };

        // Position par défaut selon l'ID
        if (defaultPositions[id]) {
            return defaultPositions[id];
        }

        // Pour les viewers et autres fenêtres dynamiques
        if (id.startsWith('viewer_')) {
            // Cas spécial pour le certificat de formation
            if (id.includes('certificat')) {
                return { x: 378, y: 32, width: 956, height: 850 };
            }
            return { x: 769, y: 32, width: 565, height: 850 };
        }

        if (id.startsWith('properties_')) {
            return { x: 250, y: 50, width: 600, height: 700 };
        }

        // Fallback général
        return { x: 100, y: 100, width: 400, height: 300 };
    });

    // Vérification et replacement automatique si la fenêtre sort de l'écran
    useEffect(() => {
        const checkWindowBounds = () => {
            const screenWidth = window.innerWidth;
            const screenHeight = window.innerHeight;
            const taskbarHeight = 41; // Hauteur de la barre des tâches (40px + 1px de bordure)
            const maxY = screenHeight - taskbarHeight;
            
            let needsAdjustment = false;
            let newRect = { ...rect };

            // Si le bord gauche de la fenêtre sort à gauche de l'écran
            if (rect.x < 0) {
                newRect.x = 0;
                needsAdjustment = true;
            }

            // Si le bord droit de la fenêtre sort à droite de l'écran
            if (rect.x + rect.width > screenWidth) {
                newRect.x = screenWidth - rect.width;
                needsAdjustment = true;
            }

            // Si le bord bas de la fenêtre sort en bas de l'écran (au-dessus de la barre des tâches)
            if (rect.y + rect.height > maxY) {
                newRect.y = maxY - rect.height;
                needsAdjustment = true;
            }

            // Appliquer l'ajustement si nécessaire
            if (needsAdjustment) {
                console.log(`Window ${id} - Repositionnement automatique:`, newRect);
                setRect(newRect);
                localStorage.setItem(`window_rect_${id}`, JSON.stringify(newRect));
            }
        };

        // Vérifier au montage et lors du redimensionnement de la fenêtre
        checkWindowBounds();
        window.addEventListener('resize', checkWindowBounds);

        return () => {
            window.removeEventListener('resize', checkWindowBounds);
        };
    }, [id, rect.x, rect.y, rect.width, rect.height]);

    // Listener pour forcer la mise à jour de la taille (pour albion-player)
    useEffect(() => {
        const handleForceUpdate = (e: Event) => {
            const customEvent = e as CustomEvent;
            if (customEvent.detail.windowId === id) {
                const savedRect = localStorage.getItem(`window_rect_${id}`);
                if (savedRect) {
                    try {
                        const newRect = JSON.parse(savedRect);
                        setRect(newRect);
                    } catch (err) {
                        console.error('Erreur parsing rect:', err);
                    }
                }
            }
        };

        window.addEventListener('FORCE_WINDOW_UPDATE', handleForceUpdate);
        return () => {
            window.removeEventListener('FORCE_WINDOW_UPDATE', handleForceUpdate);
        };
    }, [id]);

    // Interaction State
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const interactionRef = useRef<{
        type: 'drag' | 'resize' | null;
        dir: string | null;
        dragStart: { x: number, y: number } | null;
        rectStart: { x: number, y: number, w: number, h: number } | null;
    }>({
        type: null,
        dir: null,
        dragStart: null,
        rectStart: null
    });

    // Global Listeners
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            const { type, dir, dragStart, rectStart } = interactionRef.current;
            if (!type || !dragStart || !rectStart) return;

            // Vérifier si la souris est sortie des limites de la fenêtre du jeu
            const gameWindow = document.documentElement;
            const gameRect = gameWindow.getBoundingClientRect();

            if (e.clientX < 0 || e.clientX > window.innerWidth ||
                e.clientY < 0 || e.clientY > window.innerHeight) {
                // La souris est sortie de la fenêtre du jeu, arrêter le glissement
                // Réinitialiser l'état d'interaction directement
                interactionRef.current = { type: null, dir: null, dragStart: null, rectStart: null };
                setIsDragging(false);
                setIsResizing(false);
                // Sauvegarder la position finale
                localStorage.setItem(`window_rect_${id}`, JSON.stringify(rect));
                return;
            }

            const dx = e.clientX - dragStart.x;
            const dy = e.clientY - dragStart.y;

            if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
                let newRect = { ...rectStart };
                if (type === 'drag') {
                    // Déplacement avec contraintes essentielles pour garder la fenêtre accessible
                    let newX = rectStart.x + dx;
                    let newY = rectStart.y + dy;

                    // CONTRAINTES ESSENTIELLES pour garder la fenêtre accessible :

                    // 1. Ne pas passer sous la barre système (garder la barre de titre accessible)
                    const workspaceTop = 0;
                    newY = Math.max(workspaceTop, newY);

                    // 2. Contraintes latérales - empêcher la fenêtre de sortir sur les côtés
                    const screenWidth = window.innerWidth;
                    newX = Math.max(0, newX); // Ne pas sortir à gauche
                    newX = Math.min(screenWidth - newRect.w, newX); // Ne pas sortir à droite

                    // 3. Ne pas passer sous la barre des tâches
                    const taskbarHeight = 41; // Hauteur de la barre des tâches (40px + 1px de bordure)
                    const windowExtraHeight = 35; // Marge de sécurité pour bordure + ombre + espacement
                    const screenHeight = window.innerHeight;
                    const maxY = screenHeight - taskbarHeight - newRect.h - windowExtraHeight;
                    newY = Math.min(maxY, newY);

                    setRect({ x: newX, y: newY, width: newRect.w, height: newRect.h });
                } else if (type === 'resize' && dir) {
                    // Redimensionnement en temps réel
                    const minW = 200;
                    const minH = 150;
                    if (dir.includes('e')) newRect.w = Math.max(minW, rectStart.w + dx);
                    else if (dir.includes('w')) {
                        const wishW = rectStart.w - dx;
                        if (wishW > minW) { newRect.w = wishW; newRect.x = rectStart.x + dx; }
                        else { newRect.w = minW; newRect.x = rectStart.x + (rectStart.w - minW); }
                    }
                    if (dir.includes('s')) newRect.h = Math.max(minH, rectStart.h + dy);
                    else if (dir.includes('n')) {
                        const wishH = rectStart.h - dy;
                        if (wishH > minH) { newRect.h = wishH; newRect.y = rectStart.y + dy; }
                        else { newRect.h = minH; newRect.y = rectStart.y + (rectStart.h - minH); }
                    }
                    setRect({ x: newRect.x, y: newRect.y, width: newRect.w, height: newRect.h });
                }
            }
        };

        const handleMouseUp = (e: MouseEvent) => {
            const { type, dragStart, rectStart } = interactionRef.current;
            if (type && dragStart && rectStart) {
                // Sauvegarder la position finale
                localStorage.setItem(`window_rect_${id}`, JSON.stringify(rect));
                
                // LOG POUR DIMENSIONNEMENT - Afficher les dimensions finales
                console.log(`📐 WINDOW [${title}] (ID: ${id}) - Dimensions finales:`, {
                    width: rect.width,
                    height: rect.height,
                    x: rect.x,
                    y: rect.y,
                    windowId: id
                });
            }

            // Réinitialiser l'état d'interaction
            interactionRef.current = { type: null, dir: null, dragStart: null, rectStart: null };
            setIsDragging(false);
            setIsResizing(false);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [id, rect]);

    // Listener pour sauvegarde forcée de la position (spécialement pour le tableau)
    useEffect(() => {
        const handleForceSavePosition = () => {
            if (id === 'board') {
                localStorage.setItem(`window_rect_${id}`, JSON.stringify(rect));
                console.log('Position fenêtre tableau sauvegardée forcément:', rect);
            }
        };

        window.addEventListener('BOARD_SAVE_STATE', handleForceSavePosition);
        return () => window.removeEventListener('BOARD_SAVE_STATE', handleForceSavePosition);
    }, [id, rect]);

    const handleInteractionStart = (e: React.MouseEvent, type: 'drag' | 'resize', dir?: string) => {
        e.preventDefault();
        e.stopPropagation();

        // Activer la fenêtre dès qu'on commence à interagir avec elle
        // Cela permet d'avoir l'indicateur visuel correct
        if (!isActive) {
            onFocus();
        }

        // Définir l'état d'interaction
        if (type === 'drag') {
            setIsDragging(true);
        } else {
            setIsResizing(true);
        }

        interactionRef.current = {
            type,
            dir: dir || null,
            dragStart: { x: e.clientX, y: e.clientY },
            rectStart: { x: rect.x, y: rect.y, w: rect.width, h: rect.height }
        };
    };

    if (isMinimized) {
        // Toutes les fenêtres minimisées disparaissent complètement
        return null;
    }

    return (
        <>
            <div
                className={`absolute flex flex-col bg-slate-800 border-2
                    ${isActive
                        ? 'border-cyan-400 shadow-[6px_6px_0px_rgba(0,0,0,0.9)] z-50'
                        : 'border-slate-500 shadow-[3px_3px_0px_rgba(0,0,0,0.7)] z-10'
                    }
                    ${isDragging ? 'cursor-move' : ''}
                    ${isResizing ? 'select-none' : ''}
                    ${isAppearing ? 'animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4 duration-600' : ''}
                    ${id === 'albion-player' ? 'transition-[width] duration-500 ease-in-out' : ''}
                `}
                style={{
                    left: rect.x,
                    top: rect.y,
                    width: rect.width,
                    height: rect.height,
                    zIndex: zIndex, // Utiliser directement la prop zIndex passée
                    overscrollBehavior: 'none'
                }}
                onMouseDown={(e) => {
                    // Focus automatique seulement lors d'un vrai clic (pas pendant le déplacement)
                    if (!isActive && !isDragging && !isResizing) {
                        onFocus();
                    }
                }}
            >
                {/* Resize Handles - Zones plus précises (seulement si resizable) */}
                {resizable && (
                    <>
                        <div className="absolute inset-x-0 -top-2 h-3 cursor-n-resize z-[60]" onMouseDown={(e) => handleInteractionStart(e, 'resize', 'n')} />
                        <div className="absolute inset-x-0 -bottom-2 h-3 cursor-s-resize z-[60]" onMouseDown={(e) => handleInteractionStart(e, 'resize', 's')} />
                        <div className="absolute inset-y-0 -left-2 w-3 cursor-w-resize z-[60]" onMouseDown={(e) => handleInteractionStart(e, 'resize', 'w')} />
                        <div className="absolute inset-y-0 -right-2 w-3 cursor-e-resize z-[60]" onMouseDown={(e) => handleInteractionStart(e, 'resize', 'e')} />
                        <div className="absolute -top-2 -left-2 w-5 h-5 cursor-nw-resize z-[70]" onMouseDown={(e) => handleInteractionStart(e, 'resize', 'nw')} />
                        <div className="absolute -top-2 -right-2 w-5 h-5 cursor-ne-resize z-[70]" onMouseDown={(e) => handleInteractionStart(e, 'resize', 'ne')} />
                        <div className="absolute -bottom-2 -left-2 w-5 h-5 cursor-sw-resize z-[70]" onMouseDown={(e) => handleInteractionStart(e, 'resize', 'sw')} />
                        <div className="absolute -bottom-2 -right-2 w-5 h-5 cursor-se-resize z-[70]" onMouseDown={(e) => handleInteractionStart(e, 'resize', 'se')} />
                    </>
                )}

                {/* Header/Title Bar */}
                <div
                    className={`min-h-[36px] h-9 pl-2 pr-1 py-1 flex justify-between items-center select-none cursor-move border-b-2 flex-shrink-0
                        ${isActive
                            ? 'bg-gradient-to-r from-cyan-600 to-cyan-700 text-white border-cyan-300'
                            : 'bg-gradient-to-r from-slate-600 to-slate-700 text-slate-200 border-slate-400'
                        }`}
                    onMouseDown={(e) => handleInteractionStart(e, 'drag')}
                >
                    <div className="flex items-center gap-2 truncate pointer-events-none">
                        {isActive && <div className="w-2 h-2 bg-white rounded-full"></div>}
                        <span className="font-bold tracking-wider uppercase text-sm font-mono truncate">{title}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        {onHelp && (
                            <button
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => { e.stopPropagation(); onHelp(); }}
                                className="w-7 h-7 flex items-center justify-center bg-slate-500 border-2 border-slate-400 text-white text-xs hover:bg-blue-500 transition-colors font-bold flex-shrink-0"
                                title={t('windows.help')}
                            >
                                ?
                            </button>
                        )}
                        {onMinimize && (
                            <button
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => { e.stopPropagation(); onMinimize(); }}
                                className="w-7 h-7 flex items-center justify-center bg-slate-500 border-2 border-slate-400 text-white text-xs hover:bg-slate-400 transition-colors font-bold flex-shrink-0"
                                title={t('windows.minimize')}
                            >
                                _
                            </button>
                        )}
                        <button
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); onClose(); }}
                            className="w-7 h-7 flex items-center justify-center bg-slate-500 border-2 border-slate-400 text-white text-xs hover:bg-red-500 transition-colors font-bold flex-shrink-0"
                            title={t('windows.close')}
                        >
                            X
                        </button>
                    </div>
                </div>

                {/* Internal Content Area */}
                <div
                    className="flex-1 relative bg-slate-900 border-2 border-slate-600 m-1 overflow-hidden"
                    onMouseDown={(e) => {
                        // Focus lors du clic sur le contenu de la fenêtre
                        if (!isActive) {
                            e.stopPropagation();
                            onFocus();
                        }
                    }}
                >
                    {children}
                </div>
            </div>

        </>
    );
};
